import React, { FC, useState, useRef, useEffect, useCallback } from 'react';
import {
  DateRangePicker,
  RangeKeyDict,
  Range,
  createStaticRanges,
} from 'react-date-range';
import {
  format,
  addDays,
  startOfWeek,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  endOfWeek,
  endOfMonth,
  endOfQuarter,
  endOfYear,
  isSameDay,
  differenceInCalendarDays,
  startOfDay,
  endOfDay,
  subYears,
  subQuarters,
  subMonths,
} from 'date-fns';

import 'react-date-range/dist/styles.css'; // main css file
import 'react-date-range/dist/theme/default.css'; // theme css file
import { enUS } from 'date-fns/locale';
import { DateRangeInputExtProps } from './DateRangeInputExt.props';
import {
  Box,
  Button,
  Flex,
  FormControl,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  Text,
} from '@chakra-ui/react';
import { MdCalendarToday, MdClose } from 'react-icons/md';
import './DateRangeInputExt.style.css';

const DateRangeInputExt: FC<DateRangeInputExtProps> = ({
  startDate,
  endDate,
  onSearchPress,
  onSearchClear,
  searchLabel = 'Search',
}) => {
  const initialValue = [
    {
      startDate: startDate || new Date(),
      endDate: endDate || new Date(),
      key: 'selection',
    },
  ];

  const [showCalendar, setShowCalendar] = useState(false);
  const [dateRange, setDateRange] = useState<Range[]>(initialValue);
  const [selectionCount, setSelectionCount] = useState(0); // Hack:Tracks how many dates have been selected (start and end)
  const [lastInputMethod, setLastInputMethod] = useState<
    'mouse' | 'keyboard' | null
  >(null); //Hack: keep track of whether both start and end dates have been selected based on mouse clicks

  const calendarRef = useRef<HTMLDivElement>(null);
  const inputFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const changedValue = [
      {
        startDate: startDate || endDate || new Date(),
        endDate: endDate || startDate || new Date(),
        key: 'selection',
      },
    ];
    setDateRange(changedValue);

    if ((startDate || endDate) && inputFieldRef.current) {
      inputFieldRef.current.value = `${format(
        startDate || endDate || new Date(),
        'MM/dd/yyyy',
      )} - ${format(endDate || startDate || new Date(), 'MM/dd/yyyy')}`;
    } else if (inputFieldRef.current) {
      inputFieldRef.current.value = '';
    }
  }, [startDate, endDate]);

  const handleDateSelect = (ranges: RangeKeyDict) => {
    const { startDate, endDate } = ranges.selection;

    if (lastInputMethod === 'mouse') {
      setSelectionCount((prev) => prev + 1);
    }

    if (startDate && endDate && inputFieldRef.current) {
      inputFieldRef.current.value = `${format(
        startDate,
        'MM/dd/yyyy',
      )} - ${format(endDate, 'MM/dd/yyyy')}`;
    }

    setDateRange([ranges.selection]);

    // Close the calendar if both start and end dates are selected
    if (selectionCount >= 1) {
      setShowCalendar(false);
      setSelectionCount(0); // Reset the selection count
      if (startDate && endDate) onSearchPress(startDate, endDate);
    }
  };

  const handleSearch = () => {
    const startDate = dateRange[0].startDate;
    const endDate = dateRange[0].endDate;
    if (startDate && endDate) {
      setShowCalendar(false);
      setSelectionCount(0);
      onSearchPress(startDate, endDate);
    }
  };

  //trigger search when enter is pressed
  const handleEnterPress = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleSearch();
      }
    },
    [dateRange, handleSearch],
  );

  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement;

    if (target.classList.contains('rdrStaticRangeLabel')) {
      setSelectionCount((prev) => prev + 1); // Close the calendar immediately for static range selection
      return;
    }

    if (calendarRef.current && !calendarRef.current.contains(target)) {
      setShowCalendar(false);
    }
  };

  const handleInputClick = () => {
    setShowCalendar((prev) => !prev);
    setSelectionCount(0); // Reset selection count when calendar is opened
  };

  //Hack: Focus and select the contents of the static input fields when clicked
  const attachClickHandlers = () => {
    const inputElements = document.querySelectorAll('.rdrInputRangeInput');
    inputElements.forEach((input) => {
      (input as HTMLInputElement).addEventListener('click', (event) => {
        const target = event.target as HTMLInputElement;
        target.focus();
        target.select();
      });
    });
  };

  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      setLastInputMethod('mouse');
    };

    const handleKeyDown = () => {
      setLastInputMethod('keyboard');
    };

    const attachKeyDownHandler = () => {
      const inputElements = document.querySelectorAll('.rdrInputRangeInput');
      inputElements.forEach((input) => {
        (input as HTMLInputElement).addEventListener(
          'keydown',
          handleEnterPress,
        );
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);

    attachKeyDownHandler(); // Attach keydown event to static input range

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);

      // Cleanup keydown event listeners
      const inputElements = document.querySelectorAll('.rdrInputRangeInput');
      inputElements.forEach((input) => {
        (input as HTMLInputElement).removeEventListener(
          'keydown',
          handleEnterPress,
        );
      });
    };
  }, [handleEnterPress]);

  useEffect(() => {
    if (showCalendar) {
      attachClickHandlers(); // Attach click handlers when the calendar is shown
    }

    // Clean up handlers when the calendar is hidden or the component is unmounted
    return () => {
      const inputElements = document.querySelectorAll('.rdrInputRangeInput');
      inputElements.forEach((input) => {
        (input as HTMLInputElement).removeEventListener('click', (event) => {
          const target = event.target as HTMLInputElement;
          target.focus();
          target.select();
        });
      });
    };
  }, [showCalendar]);

  const customStaticRanges = createStaticRanges([
    {
      label: 'Yesterday',
      range: () => ({
        startDate: addDays(new Date(), -1),
        endDate: addDays(new Date(), -1),
      }),
    },
    {
      label: 'Last Week',
      range: () => ({
        startDate: startOfWeek(addDays(new Date(), -7)),
        endDate: endOfWeek(addDays(new Date(), -7)),
      }),
    },
    {
      label: 'Last Month',
      range: () => ({
        startDate: startOfMonth(subMonths(new Date(), 1)),
        endDate: endOfMonth(subMonths(new Date(), 1)),
      }),
    },
    {
      label: 'Last Quarter',
      range: () => ({
        startDate: startOfQuarter(subQuarters(new Date(), 1)),
        endDate: endOfQuarter(subQuarters(new Date(), 1)),
      }),
    },
    {
      label: 'Last Year',
      range: () => ({
        startDate: startOfYear(subYears(new Date(), 1)),
        endDate: endOfYear(subYears(new Date(), 1)),
      }),
    },
    {
      label: 'This Week',
      range: () => ({
        startDate: startOfWeek(new Date()),
        endDate: endOfWeek(new Date()),
      }),
    },
    {
      label: 'This Month',
      range: () => ({
        startDate: startOfMonth(new Date()),
        endDate: endOfMonth(new Date()),
      }),
    },
    {
      label: 'This Quarter',
      range: () => ({
        startDate: startOfQuarter(new Date()),
        endDate: endOfQuarter(new Date()),
      }),
    },
    {
      label: 'This Year',
      range: () => ({
        startDate: startOfYear(new Date()),
        endDate: endOfYear(new Date()),
      }),
    },
  ]);

  const customInputRanges = [
    {
      label: 'Days before today',
      range(value: any) {
        return {
          startDate: addDays(
            startOfDay(new Date()),
            (Math.max(Number(value), 1) - 1) * -1,
          ),
          endDate: endOfDay(new Date()),
        };
      },
      getCurrentValue(range: any) {
        if (!isSameDay(range.endDate, endOfDay(new Date()))) return '-';
        if (!range.startDate) return '∞';
        return (
          differenceInCalendarDays(endOfDay(new Date()), range.startDate) + 1
        );
      },
    },
    {
      label: 'Days after today',
      range(value: any) {
        const today = new Date();
        return {
          startDate: today,
          endDate: addDays(today, Math.max(Number(value), 1) - 1),
        };
      },
      getCurrentValue(range: any) {
        if (!isSameDay(range.startDate, startOfDay(new Date()))) return '-';
        if (!range.endDate) return '∞';
        return (
          differenceInCalendarDays(range.endDate, startOfDay(new Date())) + 1
        );
      },
    },
  ];

  return (
    <Flex alignItems="center">
      <Flex
        mb={6}
        align="flex-end"
        position="relative"
        ref={calendarRef}
        width="fit-content">
        <Stack spacing={1} minW="240px" maxW="240px" w="100%">
          <Text color="textColor">Start and End Date</Text>
          <FormControl>
            <InputGroup onClick={handleInputClick}>
              <Input ref={inputFieldRef} readOnly />
              <InputRightElement>
                <MdCalendarToday fill="#2FB64A" />
              </InputRightElement>
            </InputGroup>
          </FormControl>
          {showCalendar && (
            <Box boxShadow="md" position="absolute" top="70px" zIndex="10">
              <DateRangePicker
                locale={enUS}
                onChange={handleDateSelect}
                moveRangeOnFirstSelection={false}
                months={2}
                ranges={dateRange}
                direction="horizontal"
                showDateDisplay={false}
                staticRanges={customStaticRanges}
                inputRanges={customInputRanges}
              />
            </Box>
          )}
        </Stack>
        <Flex>
          {inputFieldRef?.current?.value && (
            <IconButton
              ml={3}
              aria-label="clear-search"
              icon={<MdClose size="22px" />}
              onClick={() => {
                setDateRange(initialValue);
                if (inputFieldRef.current) {
                  inputFieldRef.current.value = '';
                }
                onSearchClear?.();
              }}
            />
          )}
          <Button
            ml={3}
            minW="200px"
            isDisabled={!dateRange[0]?.startDate || !dateRange[0]?.endDate}
            onClick={handleSearch}>
            {searchLabel}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default DateRangeInputExt;
